import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth, API } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import {
  Users,
  ArrowLeft,
  RefreshCw,
  CheckSquare,
  Square,
  Send,
  Edit,
  Download,
  Search,
  Filter
} from "lucide-react";

const AdminBulkManagement = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [showBulkUpdate, setShowBulkUpdate] = useState(false);
  const [showBulkMessage, setShowBulkMessage] = useState(false);
  const [bulkUpdateField, setBulkUpdateField] = useState("subscription_tier");
  const [bulkUpdateValue, setBulkUpdateValue] = useState("");
  const [messageSubject, setMessageSubject] = useState("");
  const [messageContent, setMessageContent] = useState("");

  useEffect(() => {
    if (user?.role !== "admin") {
      navigate("/");
      return;
    }
    fetchUsers();
  }, [user, roleFilter]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (roleFilter) params.append("role", roleFilter);
      if (searchQuery) params.append("search", searchQuery);
      params.append("limit", "200");
      
      const res = await fetch(`${API}/admin/users?${params}`, { 
        credentials: "include" 
      });
      if (res.ok) {
        const data = await res.json();
        setUsers(data.users || []);
      }
    } catch (error) {
      toast.error("Failed to fetch users");
    } finally {
      setLoading(false);
    }
  };

  const toggleSelectUser = (userId) => {
    setSelectedUsers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const selectAll = () => {
    if (selectedUsers.length === filteredUsers.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(filteredUsers.map(u => u.user_id));
    }
  };

  const handleBulkUpdate = async () => {
    if (selectedUsers.length === 0) {
      toast.error("Select users first");
      return;
    }
    
    try {
      const res = await fetch(`${API}/admin/users/bulk/update`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          user_ids: selectedUsers,
          updates: { [bulkUpdateField]: bulkUpdateValue }
        })
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success(`Updated ${data.modified_count} users`);
        setShowBulkUpdate(false);
        setSelectedUsers([]);
        fetchUsers();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to update users");
      }
    } catch (error) {
      toast.error("Failed to update users");
    }
  };

  const handleBulkMessage = async () => {
    if (selectedUsers.length === 0) {
      toast.error("Select users first");
      return;
    }
    if (!messageContent.trim()) {
      toast.error("Message content required");
      return;
    }
    
    try {
      const res = await fetch(`${API}/admin/users/bulk/message`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          user_ids: selectedUsers,
          subject: messageSubject || "Message from Rekrutor Admin",
          message: messageContent
        })
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success(`Sent message to ${data.recipients} users`);
        setShowBulkMessage(false);
        setMessageSubject("");
        setMessageContent("");
        setSelectedUsers([]);
      } else {
        toast.error("Failed to send messages");
      }
    } catch (error) {
      toast.error("Failed to send messages");
    }
  };

  const handleExport = async () => {
    try {
      const res = await fetch(`${API}/admin/users/bulk/export`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          filters: { role: roleFilter || undefined },
          fields: ["user_id", "email", "name", "role", "subscription_tier", "created_at", "status"]
        })
      });
      
      if (res.ok) {
        const data = await res.json();
        const blob = new Blob([JSON.stringify(data.users, null, 2)], { type: "application/json" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `rekrutor_users_${new Date().toISOString().split("T")[0]}.json`;
        link.click();
        URL.revokeObjectURL(url);
        toast.success(`Exported ${data.total} users`);
      }
    } catch (error) {
      toast.error("Failed to export users");
    }
  };

  const filteredUsers = users.filter(u => {
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return u.name?.toLowerCase().includes(query) || u.email?.toLowerCase().includes(query);
    }
    return true;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-obsidian flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-crimson border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-obsidian">
      {/* Header */}
      <header className="fixed top-0 w-full z-50 bg-obsidian/80 backdrop-blur-md border-b border-[#222]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/admin" className="text-gray-400 hover:text-white transition-colors">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-crimson flex items-center justify-center">
                <Users className="w-5 h-5 text-white" />
              </div>
              <span className="font-heading text-xl font-bold">BULK MANAGEMENT</span>
            </div>
            {selectedUsers.length > 0 && (
              <span className="px-3 py-1 bg-crimson/20 text-crimson text-sm">
                {selectedUsers.length} Selected
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button
              onClick={handleExport}
              variant="outline"
              className="border-[#333] text-white"
              data-testid="export-users-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Export
            </Button>
            <Button
              onClick={fetchUsers}
              variant="outline"
              className="border-[#333] text-white"
            >
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      <main className="pt-24 pb-12 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          {/* Filters & Actions */}
          <div className="flex flex-wrap gap-4 mb-6">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search users..."
                className="pl-10 bg-obsidian-light border-[#333]"
                data-testid="search-users"
              />
            </div>
            <select
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              className="px-4 py-2 bg-obsidian-light border border-[#333] text-white focus:border-crimson"
              data-testid="role-filter"
            >
              <option value="">All Roles</option>
              <option value="jobseeker">Job Seekers</option>
              <option value="recruiter">Recruiters</option>
              <option value="admin">Admins</option>
            </select>
            
            {selectedUsers.length > 0 && (
              <>
                <Button
                  onClick={() => setShowBulkUpdate(true)}
                  className="bg-blue-500 hover:bg-blue-600 text-white"
                  data-testid="bulk-update-btn"
                >
                  <Edit className="w-4 h-4 mr-2" />
                  Bulk Update
                </Button>
                <Button
                  onClick={() => setShowBulkMessage(true)}
                  className="bg-crimson hover:bg-crimson/90 text-white"
                  data-testid="bulk-message-btn"
                >
                  <Send className="w-4 h-4 mr-2" />
                  Send Message
                </Button>
              </>
            )}
          </div>

          {/* Users Table */}
          <div className="bg-obsidian-light border border-[#222] overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-obsidian border-b border-[#333]">
                    <th className="px-4 py-3 text-left">
                      <button
                        onClick={selectAll}
                        className="flex items-center gap-2 text-gray-400 hover:text-white"
                        data-testid="select-all"
                      >
                        {selectedUsers.length === filteredUsers.length && filteredUsers.length > 0 ? (
                          <CheckSquare className="w-4 h-4 text-crimson" />
                        ) : (
                          <Square className="w-4 h-4" />
                        )}
                        <span className="text-xs uppercase tracking-wider">Select All</span>
                      </button>
                    </th>
                    <th className="px-4 py-3 text-left text-xs uppercase tracking-wider text-gray-500">User</th>
                    <th className="px-4 py-3 text-left text-xs uppercase tracking-wider text-gray-500">Role</th>
                    <th className="px-4 py-3 text-left text-xs uppercase tracking-wider text-gray-500">Subscription</th>
                    <th className="px-4 py-3 text-left text-xs uppercase tracking-wider text-gray-500">Status</th>
                    <th className="px-4 py-3 text-left text-xs uppercase tracking-wider text-gray-500">Created</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map((u, i) => (
                    <motion.tr
                      key={u.user_id}
                      className={`border-b border-[#222] hover:bg-obsidian/50 ${
                        selectedUsers.includes(u.user_id) ? "bg-crimson/10" : ""
                      }`}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: i * 0.01 }}
                      data-testid={`user-row-${u.user_id}`}
                    >
                      <td className="px-4 py-3">
                        <button
                          onClick={() => toggleSelectUser(u.user_id)}
                          className="text-gray-400 hover:text-white"
                        >
                          {selectedUsers.includes(u.user_id) ? (
                            <CheckSquare className="w-4 h-4 text-crimson" />
                          ) : (
                            <Square className="w-4 h-4" />
                          )}
                        </button>
                      </td>
                      <td className="px-4 py-3">
                        <div>
                          <div className="font-medium text-white">{u.name || "Unknown"}</div>
                          <div className="text-gray-500 text-sm">{u.email}</div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-xs px-2 py-1 ${
                          u.role === "recruiter" ? "bg-purple-500/20 text-purple-400" :
                          u.role === "jobseeker" ? "bg-green-500/20 text-green-400" :
                          u.role === "admin" ? "bg-crimson/20 text-crimson" :
                          "bg-gray-500/20 text-gray-400"
                        }`}>
                          {u.role || "unset"}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-400 text-sm">
                        {u.subscription_tier || "free"}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-xs px-2 py-1 ${
                          u.status === "banned" ? "bg-red-500/20 text-red-400" :
                          u.status === "deleted" ? "bg-gray-500/20 text-gray-400" :
                          "bg-green-500/20 text-green-400"
                        }`}>
                          {u.status || "active"}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-500 text-sm">
                        {u.created_at ? new Date(u.created_at).toLocaleDateString() : "Unknown"}
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {filteredUsers.length === 0 && (
              <div className="text-center py-12">
                <Users className="w-12 h-12 text-gray-600 mx-auto mb-3" />
                <p className="text-gray-500">No users found</p>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Bulk Update Modal */}
      {showBulkUpdate && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <motion.div
            className="bg-obsidian-light border border-[#222] p-6 max-w-md w-full"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
          >
            <h3 className="font-heading text-xl font-bold mb-4">Bulk Update</h3>
            <p className="text-gray-400 text-sm mb-4">
              Updating {selectedUsers.length} user(s)
            </p>
            
            <div className="mb-4">
              <label className="block text-sm text-gray-400 mb-2">Field to Update</label>
              <select
                value={bulkUpdateField}
                onChange={(e) => setBulkUpdateField(e.target.value)}
                className="w-full px-4 py-2 bg-obsidian border border-[#333] text-white"
                data-testid="bulk-update-field"
              >
                <option value="subscription_tier">Subscription Tier</option>
                <option value="status">Status</option>
                <option value="role">Role</option>
              </select>
            </div>
            
            <div className="mb-6">
              <label className="block text-sm text-gray-400 mb-2">New Value</label>
              {bulkUpdateField === "subscription_tier" && (
                <select
                  value={bulkUpdateValue}
                  onChange={(e) => setBulkUpdateValue(e.target.value)}
                  className="w-full px-4 py-2 bg-obsidian border border-[#333] text-white"
                  data-testid="bulk-update-value"
                >
                  <option value="">Select tier</option>
                  <option value="jobseeker_free">Job Seeker Free</option>
                  <option value="jobseeker_plus">Job Seeker Plus</option>
                  <option value="jobseeker_pro">Job Seeker Pro</option>
                  <option value="recruiter_starter">Recruiter Starter</option>
                  <option value="recruiter_pro">Recruiter Pro</option>
                  <option value="recruiter_elite">Recruiter Elite</option>
                </select>
              )}
              {bulkUpdateField === "status" && (
                <select
                  value={bulkUpdateValue}
                  onChange={(e) => setBulkUpdateValue(e.target.value)}
                  className="w-full px-4 py-2 bg-obsidian border border-[#333] text-white"
                >
                  <option value="">Select status</option>
                  <option value="active">Active</option>
                  <option value="suspended">Suspended</option>
                  <option value="banned">Banned</option>
                </select>
              )}
              {bulkUpdateField === "role" && (
                <select
                  value={bulkUpdateValue}
                  onChange={(e) => setBulkUpdateValue(e.target.value)}
                  className="w-full px-4 py-2 bg-obsidian border border-[#333] text-white"
                >
                  <option value="">Select role</option>
                  <option value="jobseeker">Job Seeker</option>
                  <option value="recruiter">Recruiter</option>
                </select>
              )}
            </div>
            
            <div className="flex gap-3">
              <Button
                onClick={() => setShowBulkUpdate(false)}
                variant="outline"
                className="flex-1 border-[#333] text-gray-400"
              >
                Cancel
              </Button>
              <Button
                onClick={handleBulkUpdate}
                className="flex-1 bg-crimson hover:bg-crimson/90 text-white"
                data-testid="confirm-bulk-update"
              >
                Update {selectedUsers.length} Users
              </Button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Bulk Message Modal */}
      {showBulkMessage && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <motion.div
            className="bg-obsidian-light border border-[#222] p-6 max-w-lg w-full"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
          >
            <h3 className="font-heading text-xl font-bold mb-4">Send Bulk Message</h3>
            <p className="text-gray-400 text-sm mb-4">
              Sending to {selectedUsers.length} user(s)
            </p>
            
            <div className="mb-4">
              <label className="block text-sm text-gray-400 mb-2">Subject</label>
              <Input
                value={messageSubject}
                onChange={(e) => setMessageSubject(e.target.value)}
                placeholder="Message subject..."
                className="bg-obsidian border-[#333]"
                data-testid="message-subject"
              />
            </div>
            
            <div className="mb-6">
              <label className="block text-sm text-gray-400 mb-2">Message</label>
              <textarea
                value={messageContent}
                onChange={(e) => setMessageContent(e.target.value)}
                placeholder="Your message to users..."
                className="w-full px-4 py-2 bg-obsidian border border-[#333] text-white focus:border-crimson focus:outline-none h-32"
                data-testid="message-content"
              />
            </div>
            
            <div className="flex gap-3">
              <Button
                onClick={() => setShowBulkMessage(false)}
                variant="outline"
                className="flex-1 border-[#333] text-gray-400"
              >
                Cancel
              </Button>
              <Button
                onClick={handleBulkMessage}
                className="flex-1 bg-crimson hover:bg-crimson/90 text-white"
                data-testid="send-bulk-message"
              >
                <Send className="w-4 h-4 mr-2" />
                Send to {selectedUsers.length} Users
              </Button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default AdminBulkManagement;
